import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    setIsMobile(mq.matches)
    return () => mq.removeEventListener('change', handler)
  }, [breakpoint])
  return isMobile
}

const API = import.meta.env.VITE_API_URL ?? ''

interface Location {
  id: string
  name: string
}

interface Rival {
  competitor_id: string
  name: string
  tracked_at_locations: number
  last_signal_at: string | null
}

interface LocationsResponse {
  locations: Location[]
  total: number
  limit: number
  offset: number
}

interface RivalsResponse {
  success: boolean
  data: { rivals: Rival[]; total: number }
  error: string | null
}

function formatSignal(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

function SignalCell({ iso }: { iso: string | null }) {
  if (!iso) return <>—</>
  return <time dateTime={iso}>{formatSignal(iso)}</time>
}

function locationLabel(n: number): string {
  return n === 1 ? '1 location' : `${n} locations`
}

// ── Skeleton ───────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <tr>
      {[80, 60, 120, 40].map((w, i) => (
        <td key={i} style={{ padding: '12px 16px' }}>
          <div style={{
            height: 14, width: w, borderRadius: 4,
            background: 'var(--border-1)', opacity: 0.5,
          }} />
        </td>
      ))}
    </tr>
  )
}

export default function Rivals() {
  const authFetch = useAuthFetch()
  const [searchParams, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()

  const [locations, setLocations] = useState<Location[]>([])
  const [rivals, setRivals] = useState<Rival[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Restore location filter from URL on mount
  const initialLocationId = searchParams.get('location_id') ?? ''
  const [selectedLocationId, setSelectedLocationId] = useState(initialLocationId)

  // Sequence counter: prevents a slow earlier fetch from overwriting a newer one
  const seqRef = useRef(0)

  const fetchRivals = useCallback(
    async (locationId: string) => {
      const seq = ++seqRef.current
      setLoading(true)
      setError(null)
      try {
        const url = locationId
          ? `${API}/api/v1/rivals/tracked?location_id=${encodeURIComponent(locationId)}`
          : `${API}/api/v1/rivals/tracked`
        const res = await authFetch(url, { signal: AbortSignal.timeout(15_000) })
        if (seq !== seqRef.current) return
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`)
        }
        const data: RivalsResponse = await res.json()
        if (seq !== seqRef.current) return
        if (!data.success) throw new Error(data.error ?? 'Unexpected error loading rivals')
        setRivals(data.data.rivals)
      } catch (err: unknown) {
        if (seq !== seqRef.current) return
        const msg = err instanceof Error ? err.message : 'Unknown error'
        setError(`Couldn't load rivals. ${msg}`)
      } finally {
        if (seq === seqRef.current) setLoading(false)
      }
    },
    [authFetch],
  )

  // Load locations once on mount
  useEffect(() => {
    let active = true
    authFetch(`${API}/api/v1/locations`)
      .then(async (res) => {
        if (!active) return
        if (!res.ok) return
        const data: LocationsResponse = await res.json()
        if (active) setLocations(data.locations ?? [])
      })
      .catch(() => { /* locations failure is non-fatal — filter just won't render */ })
    return () => { active = false }
  }, [authFetch])

  // Fetch rivals whenever selected location changes (including initial load)
  useEffect(() => {
    void fetchRivals(selectedLocationId)
  }, [fetchRivals, selectedLocationId])

  function handleLocationChange(locationId: string) {
    setSelectedLocationId(locationId)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (locationId) {
        next.set('location_id', locationId)
      } else {
        next.delete('location_id')
      }
      return next
    }, { replace: true })
  }

  const showFilter = locations.length > 1

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'var(--sans)', maxWidth: 960 }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-1)', fontFamily: 'var(--sans)' }}>
          Rivals
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-3)' }}>
          Actively tracked rivals across your locations
        </p>
      </div>

      {/* Location filter — only when org has more than one location */}
      {showFilter && (
        <div style={{ marginBottom: 20 }}>
          <select
            aria-label="Filter by location"
            value={selectedLocationId}
            onChange={(e) => handleLocationChange(e.target.value)}
            style={{
              padding: '7px 12px', borderRadius: 6, fontSize: 13,
              border: '1px solid var(--border-2)',
              background: 'var(--surface-1)', color: 'var(--text-1)',
              fontFamily: 'var(--sans)', cursor: 'pointer',
            }}
          >
            <option value="">All locations</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div style={{ borderRadius: 8, border: '1px solid var(--border-1)', background: 'var(--surface-1)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-1)' }}>
                {['Rival', 'Locations', 'Last Signal', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </tbody>
          </table>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div style={{ padding: '24px', borderRadius: 8, border: '1px solid var(--border-1)', background: 'var(--surface-1)', color: 'var(--text-1)' }}>
          <p style={{ margin: '0 0 12px', fontSize: 14 }}>{error}</p>
          <button
            onClick={() => fetchRivals(selectedLocationId)}
            style={{
              padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              background: 'var(--accent-intel)', color: '#fff',
              border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && rivals?.length === 0 && (
        <div style={{ padding: '40px 24px', borderRadius: 8, border: '1px solid var(--border-1)', background: 'var(--surface-1)', textAlign: 'center' }}>
          <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--text-2)' }}>
            No rivals tracked yet.
          </p>
          <Link
            to="/setup/competitors"
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-intel)', textDecoration: 'none' }}
          >
            Add rivals →
          </Link>
        </div>
      )}

      {/* Desktop table — ≥768px */}
      {!loading && !error && rivals && rivals.length > 0 && !isMobile && (
        <div
          style={{ borderRadius: 8, border: '1px solid var(--border-1)', background: 'var(--surface-1)', overflow: 'hidden' }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-1)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>Rival</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>Locations</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>Last Signal</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', fontFamily: 'var(--mono)' }}></th>
              </tr>
            </thead>
            <tbody>
              {rivals.map((rival) => (
                <tr key={rival.competitor_id} style={{ borderBottom: '1px solid var(--border-1)' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <Link
                      to={`/competitors/${rival.competitor_id}`}
                      style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: 14, textDecoration: 'none', fontFamily: 'var(--sans)' }}
                    >
                      {rival.name}
                    </Link>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-2)', fontFamily: 'var(--sans)' }}>
                    {locationLabel(rival.tracked_at_locations)}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>
                    <SignalCell iso={rival.last_signal_at} />
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <Link
                      to={`/competitors/${rival.competitor_id}`}
                      style={{ fontSize: 13, color: 'var(--accent-intel)', textDecoration: 'none', fontWeight: 500, fontFamily: 'var(--sans)' }}
                    >
                      → Profile
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile card list — <768px */}
      {!loading && !error && rivals && rivals.length > 0 && isMobile && (
        <div>
          {rivals.map((rival) => (
            <div
              key={rival.competitor_id}
              style={{
                padding: '16px', borderRadius: 8,
                border: '1px solid var(--border-1)', background: 'var(--surface-1)',
                marginBottom: 12,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <Link
                  to={`/competitors/${rival.competitor_id}`}
                  style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: 15, textDecoration: 'none', fontFamily: 'var(--sans)' }}
                >
                  {rival.name}
                </Link>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 4, fontFamily: 'var(--sans)' }}>
                {locationLabel(rival.tracked_at_locations)}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12, fontFamily: 'var(--mono)' }}>
                <SignalCell iso={rival.last_signal_at} />
              </div>
              <Link
                to={`/competitors/${rival.competitor_id}`}
                style={{ fontSize: 13, color: 'var(--accent-intel)', textDecoration: 'none', fontWeight: 500, fontFamily: 'var(--sans)' }}
              >
                → Profile
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
